import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useT } from "@/i18n";
import { QRCodeSVG } from "qrcode.react";
import { Logo } from "@/components/Logo";
import { Coins, DownloadSimple, CheckCircle, CloudArrowUp } from "@phosphor-icons/react";

export default function ServerMonitor() {
  const t = useT();
  const [s, setS] = useState(null);

  useEffect(() => {
    let active = true;
    const load = () => api.get("/offline/status").then(({ data }) => { if (active) setS(data); }).catch(() => {});
    load();
    const id = setInterval(load, 10000);
    return () => { active = false; clearInterval(id); };
  }, []);

  const downloadResults = async () => {
    try {
      const { data } = await api.get("/offline/results");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "kihap-results.json";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { /* ignore */ }
  };

  if (s && !s.offline) {
    return <div className="min-h-screen bg-background flex items-center justify-center p-8 text-center text-muted-foreground" data-testid="server-monitor-offline-only">{t("srv.offlineOnly")}</div>;
  }

  const url = s?.server_url || (typeof window !== "undefined" ? window.location.origin : "");
  const bal = s?.balance ?? 0;
  const balColor = bal < 10 ? (bal < 5 ? "text-hong" : "text-gold") : "text-foreground";
  const syncedAt = s?.synced_at ? new Date(s.synced_at).toLocaleString() : null;

  return (
    <div className="min-h-screen bg-background text-foreground p-8 flex flex-col gap-8" data-testid="server-monitor">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Logo size={38} className="text-brand-yellow" />
          <div>
            <div className="font-heading font-extrabold text-2xl">KIHAP</div>
            {s?.event_name && <div className="text-muted-foreground text-sm">{s.event_name}</div>}
          </div>
        </div>
        <div className={`flex items-center gap-2 font-monot font-bold text-xl ${balColor}`} data-testid="server-monitor-balance">
          <Coins weight="fill" className="text-gold" size={22} /> {bal}
        </div>
      </div>

      <div className="flex flex-col items-center justify-center gap-4">
        <div className="bg-white p-5 rounded-2xl" data-testid="server-monitor-qr">
          <QRCodeSVG value={url} size={260} />
        </div>
        <p className="text-lg font-semibold">{t("srv.scan")}</p>
        <p className="font-monot text-2xl text-brand-yellow break-all text-center">{url}</p>
      </div>

      <div>
        <h2 className="text-sm uppercase tracking-widest text-muted-foreground mb-3">{t("ev.tabRings")} · {t("srv.judgesScan")}</h2>
        {(s?.rings || []).length === 0
          ? <p className="text-muted-foreground text-sm" data-testid="server-monitor-no-rings">{t("srv.noRings")}</p>
          : <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {s.rings.map((r) => (
                <div key={r.id} className="border border-border rounded-lg p-4 bg-card flex flex-col items-center gap-2" data-testid={`server-monitor-ring-${r.id}`}>
                  <div className="bg-white p-2 rounded-lg"><QRCodeSVG value={`${url}/judge/${s.event_id}/${r.id}`} size={104} /></div>
                  <span className="font-semibold text-sm text-center">{r.name}</span>
                </div>
              ))}
            </div>}
      </div>

      <div className="mt-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-border pt-5" data-testid="server-monitor-sync">
        <div className="flex items-center gap-2 text-sm">
          {syncedAt ? (
            <span className="flex items-center gap-2 text-brand-yellow" data-testid="server-monitor-synced">
              <CheckCircle weight="fill" size={18} /> {t("srv.syncedAt").replace("{t}", syncedAt)}
            </span>
          ) : (
            <span className="flex items-center gap-2 text-muted-foreground" data-testid="server-monitor-not-synced">
              <CloudArrowUp size={18} /> {s?.can_autosync ? t("srv.notSynced") : ""}
            </span>
          )}
        </div>
        <button onClick={downloadResults} data-testid="server-monitor-download-results"
          className="inline-flex items-center justify-center gap-2 rounded-md bg-secondary px-4 py-2 text-sm font-semibold hover:bg-secondary/80 transition-colors">
          <DownloadSimple size={16} /> {t("srv.downloadResults")}
        </button>
      </div>
    </div>
  );
}
